import { fetchAuthSession } from '@aws-amplify/auth';
import config from '../utils/Constants';

// NOTE: The backend SSE view (sse_views.py) currently accepts session auth or X-API-Key.
// Until the backend is updated to also accept Bearer tokens on the SSE endpoints,
// this call will return 401 in production. Update sse_views.py to check:
//   Authorization: Bearer <token>  via DRF's JWTAuthentication
// once the backend feat/bedrock-streaming-admissions branch is deployed.

export const SECTIONS_ORDER = [
  'history_of_present_illness',
  'assessment_and_plan',
  'physical_exam',
  'medical_history',
  'progress_note',
  'medications',
  'lab_report',
  'imaging',
  'discharge_summary',
];

export const SECTION_LABELS = {
  history_of_present_illness: 'History of Present Illness',
  assessment_and_plan: 'Assessment & Plan',
  physical_exam: 'Physical Examination',
  medical_history: 'Past Medical History',
  progress_note: 'Progress Notes',
  medications: 'Medications',
  lab_report: 'Labs',
  imaging: 'Imaging',
  discharge_summary: 'Discharge Summary',
};

/**
 * Opens an SSE stream for admission generation and dispatches events to callbacks.
 *
 * @param {string} admissionUuid
 * @param {{ onSectionStart, onToken, onSectionEnd, onSectionError, onDone, onError }} callbacks
 * @returns {Promise<void>} resolves when stream closes
 */
export async function streamAdmissionGeneration(admissionUuid, callbacks, signal, streamingUrl) {
  const session = await fetchAuthSession();
  if (!session.tokens?.idToken) throw new Error('Not authenticated');
  const token = session.tokens.idToken.toString();
  // SSE views live at /medchart/ (plain Django), not /api/v1/ (Ninja).
  // Priority: per-tenant streaming_url from /me/ → REACT_APP_SSE_BASE_URL (local dev) → base URL.
  const baseUrl = streamingUrl || process.env.REACT_APP_SSE_BASE_URL || config.getBaseUrl();

  const response = await fetch(
    `${baseUrl}/medchart/stream/admission/${admissionUuid}/generate`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'text/event-stream',
      },
      signal,
    }
  );

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`Stream request failed: ${response.status} ${text}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let currentEvent = null;

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop();

      for (const line of lines) {
        if (line.startsWith('event: ')) {
          currentEvent = line.slice(7).trim();
        } else if (line.startsWith('data: ')) {
          let data;
          try {
            data = JSON.parse(line.slice(6).trim());
          } catch {
            continue;
          }

          switch (currentEvent) {
            case 'section_start':
              callbacks.onSectionStart?.(data);
              break;
            case 'token':
              callbacks.onToken?.(data);
              break;
            case 'section_end':
              callbacks.onSectionEnd?.(data);
              break;
            case 'error':
              callbacks.onSectionError?.(data);
              break;
            case 'done':
              callbacks.onDone?.(data);
              break;
            default:
              break;
          }
          currentEvent = null;
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}
